// Gets an agent asset prefix for custom domains
import CheckoutCheckboxes from '@/components/virgin/CheckoutCheckboxes'
import CustomCheckoutCheckboxes from '@/components/CheckoutCheckboxes'
import CheckoutForm from '@/components/virgin/CheckoutForm'
import OrderSummary from '@/components/virgin/OrderSummary'
import VelocityPromisePoints from '@/components/VirginPromisePoints'
import { CheckoutFormValue, TemplateDefinition } from '@/types'
import { FormikValues, FormikHelpers } from 'formik'
import GCSummerPromisePoints from '@/components/gcsummerfunds/PromisePoints'
import CheckoutTerms from '@/components/CheckoutTerms'
import { Spacing } from '@big-red-group/storefront-common-ui'
import { logger } from '@/logging/Logger'

type CustomBehaviourOptions =
  | {
      renderNZPrivacyDisclosureCheckbox?: true | false
      renderCheckoutCheckboxes: true
      renderCheckoutForm?: false
      renderOrderSummary?: false
      renderHomePromisePoints?: false
      setBurnLoyaltyPoints?: undefined
      setLoyaltyOperationType?: undefined
      setAllowProceedCheckout: React.Dispatch<React.SetStateAction<boolean>>
      loyaltyCampaignData?: undefined
    }
  | {
      renderCheckoutCheckboxes?: false
      renderCheckoutForm: true
      renderOrderSummary?: false
      renderHomePromisePoints?: false
      setBurnLoyaltyPoints: React.Dispatch<React.SetStateAction<number>>
      setLoyaltyOperationType: React.Dispatch<React.SetStateAction<string>>
      setAllowProceedCheckout: React.Dispatch<React.SetStateAction<boolean>>
      loyaltyCampaignData?: undefined
      onBlur: () => void
    }
  | {
      renderCheckoutCheckboxes?: false
      renderCheckoutForm?: false
      renderOrderSummary: true
      renderHomePromisePoints?: false
      setBurnLoyaltyPoints?: undefined
      setLoyaltyOperationType?: undefined
      setAllowProceedCheckout?: undefined
      loyaltyCampaignData: {
        virgin?: {
          velocityPointsType: 'earn' | 'burn'
          burnloyaltyPoints: number
        }
      }
    }
  | {
      renderCheckoutCheckboxes?: false
      renderCheckoutForm?: false
      renderOrderSummary?: false
      renderHomePromisePoints: true
      setBurnLoyaltyPoints?: undefined
      setLoyaltyOperationType?: undefined
      setAllowProceedCheckout?: undefined
      loyaltyCampaignData?: undefined
    }

export const getCustomBehaviour = (
  handlerKey: string | undefined,
  behaviourOptions: CustomBehaviourOptions,
  displayNZRiskDisclosureStatement = false
) => {
  let componentToRender = null

  switch (handlerKey) {
    case 'virgin':
      if (behaviourOptions.renderCheckoutCheckboxes) {
        componentToRender = (
          <>
            <CheckoutCheckboxes allowProceedCheckout={behaviourOptions.setAllowProceedCheckout!} />
            <Spacing />
          </>
        )
      } else if (behaviourOptions.renderCheckoutForm) {
        componentToRender = (
          <>
            <CheckoutForm
              setBurnLoyaltyPoints={behaviourOptions.setBurnLoyaltyPoints}
              setLoyaltyOperationType={behaviourOptions.setLoyaltyOperationType}
              setAllowProceedCheckout={behaviourOptions.setAllowProceedCheckout}
              onBlur={behaviourOptions.onBlur}
            />
          </>
        )
      } else if (behaviourOptions.renderOrderSummary) {
        componentToRender = (
          <>
            <OrderSummary
              velocityPointsType={behaviourOptions.loyaltyCampaignData?.[handlerKey]?.velocityPointsType!}
              burnLoyaltyPoints={behaviourOptions.loyaltyCampaignData?.[handlerKey]?.burnloyaltyPoints!}
            />
          </>
        )
      }
      break

    case 'gcsummerfunds':
      if (behaviourOptions.renderCheckoutCheckboxes) {
        componentToRender = (
          <CustomCheckoutCheckboxes allowProceedCheckout={behaviourOptions.setAllowProceedCheckout!} />
        )
      } else if (behaviourOptions.renderHomePromisePoints) {
        componentToRender = <GCSummerPromisePoints />
      }
      break

    case 'nbr':
      if (behaviourOptions.renderCheckoutCheckboxes) {
        componentToRender = (
          <CheckoutTerms
            termsOwner="Neighbourhood Business Recharge"
            termsUrl="https://cdn.experienceoz.com.au/assets/catalogueType/2/miscPDF/161037.pdf"
            privacyPolicyUrl={`/en/privacy-policy`}
          />
        )
      }
      break

    default:
      break
  }

  if (displayNZRiskDisclosureStatement && behaviourOptions.renderCheckoutCheckboxes) {
    return (
      <>
        {componentToRender}
        <CustomCheckoutCheckboxes allowProceedCheckout={behaviourOptions.setAllowProceedCheckout!} />
      </>
    )
  }

  return componentToRender
}

export const getCustomCheckoutValidation = (
  partnerCode: string | undefined,
  fields: FormikValues,
  actions: FormikHelpers<CheckoutFormValue>
) => {
  const postcode = parseInt(fields.postcode)
  switch (partnerCode) {
    case 'gcsummerfunds':
      if (!fields.postcode) {
        actions.setFieldError(
          'region',
          "Unfortunately your region is outside of the designated zone for the GC Summer FUNds promotion. But don't worry, have $10* on us to still enjoy an epic Gold Coast experience. Use promo code COMEPLAY at <a href='https://www.experienceoz.com.au/en' target='_blank'>Experience OZ</a> Minimum spend $100."
        )
        return false
      }
      if ((postcode >= 4000 && postcode <= 4350) || (postcode >= 4499 && postcode <= 4601)) {
        return true
      } else {
        actions.setFieldError(
          'postcode',
          "Unfortunately your postcode is outside of the designated zone for the GC Summer FUNds promotion. But don't worry, have $10* on us to still enjoy an epic Gold Coast experience. Use promo code COMEPLAY at <a href='https://www.experienceoz.com.au/en' target='_blank'>Experience OZ</a> Minimum spend $100."
        )
        return false
      }
    default:
      return true
  }
}

export const getAvailabilityBlockout = (partnerCode: string | undefined, date: Date) => {
  switch (partnerCode) {
    case 'gcsummerfunds':
      return date.getTime() <= new Date(2024, 2, 29).getTime()
    case 'tropicaldollars':
      return date.getTime() <= new Date(2024, 5, 21).getTime()
    // If no blockouts, true enables all dates
    default:
      return true
  }
}

export const WhiteLabelDefaultHomePage = (categories: any, regions: any): TemplateDefinition => {
  return {
    widgets: [
      {
        name: 'home-banner-minimal',
        title: 'Experience good times',
        subtitle: 'Book the best value tours, attractions & things to do across Australia & NZ',
        banners: [
          {
            heading: '',
            cta: 'Find deals',
            image: 'https://cdn.experienceoz.com.au/assets/catalogueType/2/miscGraph/158017.jpg',
            link: '/en/things-to-do/hot-deals',
          },
        ],
      },
      {
        name: 'region-carousel',
        label: 'Take me there',
        regions,
      },
      {
        name: 'category-grid',
        label: "Experiences we think you'll love",
        categories,
      },
    ],
  }
}

export async function addVAMetadata(transformedExtraFields: { key: string; value: any }[], baseUrl: string) {
  const log = logger()
  try {
    const res = await fetch(`${baseUrl}/api/cart/add-metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(transformedExtraFields),
    })

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`)
    }
    await res.json()
  } catch (error) {
    log.error('Error adding metadata')
  }
}
